/**
 * Secure API Utility with Cookie-based Authentication
 * 
 * Automatically sends cookies with all requests for httpOnly cookie auth.
 * No manual token management needed on frontend.
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Fetch wrapper with credentials for cookie-based auth
 * 
 * @param {string} url - API endpoint (with or without /api prefix)
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>}
 */
export async function secureFetch(url, options = {}) {
  const fullUrl = url.startsWith('http') 
    ? url 
    : `${API_BASE_URL}${url.startsWith('/api') ? url : `/api${url}`}`;
  
  return fetch(fullUrl, {
    ...options,
    credentials: 'include',  // Always send cookies
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    }
  });
}

/**
 * POST request with automatic JSON handling
 */
export async function securePost(url, data, options = {}) {
  const response = await secureFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
}

/**
 * GET request with automatic JSON parsing
 */
export async function secureGet(url, options = {}) {
  const response = await secureFetch(url, {
    method: 'GET',
    ...options
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  
  return response.json();
}

/**
 * One-time cleanup of old localStorage tokens
 * Call this once on app init.
 *
 * Only keys nothing writes any more belong here. The POS, admin, company-admin
 * and onboarding apps still keep their sessions in localStorage; removing
 * those keys on every load signed users out after each refresh.
 */
export function cleanupLegacyTokens() {
  const legacyKeys = [
    'access_token',
  ];
  
  legacyKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`[Security] Removing legacy token: ${key}`);
      localStorage.removeItem(key);
    }
  });
}

export default {
  secureFetch,
  securePost,
  secureGet,
  cleanupLegacyTokens
};
